import { ALERT_SEVERITIES, type Alert, FEED_PROVIDERS, OPS_ALERT_KINDS } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  type Column,
  DataTable,
  EmptyState,
  PageHeader,
  Select,
  useToast,
} from '@fruuit/ui'
import { Check, CircleCheck } from 'lucide-react'
import { useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'
import { type AlertFilters, useAcknowledgeAlert, useAlerts } from '../../features/alerts/api'
import { SeverityBadge } from '../../features/alerts/components'
import { formatDateTime, formatRelative } from '../../features/feeds/format'
import { useCursorPager } from '../../features/feeds/useCursorPager'
import { useTitle } from '../../lib/useTitle'

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'all', label: 'All alerts' },
]
const SEVERITY_OPTIONS = [
  { value: '', label: 'Any severity' },
  ...ALERT_SEVERITIES.values.map((value) => ({ value, label: ALERT_SEVERITIES.labels[value] })),
]
const KIND_OPTIONS = [
  { value: '', label: 'Any kind' },
  ...OPS_ALERT_KINDS.values.map((value) => ({ value, label: OPS_ALERT_KINDS.labels[value] })),
]
const PROVIDER_OPTIONS = [
  { value: '', label: 'Any source' },
  ...FEED_PROVIDERS.values.map((value) => ({ value, label: FEED_PROVIDERS.labels[value] })),
]

function AcknowledgeButton({ alert }: { alert: Alert }) {
  const { toast } = useToast()
  const acknowledge = useAcknowledgeAlert()
  return (
    <Button
      size="sm"
      variant="secondary"
      icon={<Check />}
      loading={acknowledge.isPending}
      aria-label={`Acknowledge: ${alert.subject}`}
      onClick={() => {
        acknowledge
          .mutateAsync({ id: alert.id, revision: alert.revision })
          .then(() => toast({ tone: 'success', message: 'Alert acknowledged.' }))
          .catch((error: unknown) =>
            toast({
              tone: 'error',
              title: 'Not acknowledged',
              message: isApiError(error, 'forbidden')
                ? 'Only platform admins can acknowledge alerts.'
                : isApiError(error, 'stale_revision')
                  ? 'Someone else changed this alert. The list has been refreshed.'
                  : errorMessage(error),
            }),
          )
      }}
    >
      Acknowledge
    </Button>
  )
}

const COLUMNS: Column<Alert>[] = [
  {
    id: 'severity',
    header: 'Severity',
    cell: (alert) => <SeverityBadge severity={alert.severity} />,
  },
  {
    id: 'alert',
    header: 'Alert',
    cell: (alert) => (
      <div className="min-w-[16rem] max-w-xl">
        <p className="font-semibold leading-snug text-ink">{alert.subject}</p>
        <p className="mt-1 text-xs leading-relaxed text-ink-soft">{alert.body}</p>
        <p className="mt-1.5 flex flex-wrap gap-1.5">
          <Badge tone="outline">{alert.kind_label}</Badge>
          {alert.org_count > 0 ? (
            <Badge tone="outline">
              Held by {alert.org_count} investor org{alert.org_count === 1 ? '' : 's'}
            </Badge>
          ) : null}
          {alert.emailed_at ? <Badge tone="outline">Emailed to ops</Badge> : null}
        </p>
      </div>
    ),
  },
  {
    id: 'source',
    header: 'Source',
    cell: (alert) => (
      <span className="whitespace-nowrap text-ink-soft">{alert.provider_name ?? 'Platform'}</span>
    ),
  },
  {
    id: 'raised',
    header: 'Raised',
    cell: (alert) => (
      <div className="whitespace-nowrap">
        <p className="text-ink">{formatRelative(alert.created_at)}</p>
        <p className="text-xs text-sage">{formatDateTime(alert.created_at)}</p>
      </div>
    ),
  },
  {
    id: 'state',
    header: 'Status',
    cell: (alert) =>
      alert.acknowledged_at ? (
        <div className="text-xs leading-relaxed text-sage">
          <p className="font-semibold text-ink-soft">Acknowledged</p>
          <p>
            {alert.acknowledged_by_name ?? 'A platform admin'},{' '}
            {formatDateTime(alert.acknowledged_at)}
          </p>
        </div>
      ) : (
        <AcknowledgeButton alert={alert} />
      ),
  },
]

/** M-03 / M-05 Alerts: filter and acknowledge operational alerts (P-FEED-005). */
export function AlertsPage() {
  useTitle('Alerts', 'Admin')
  const [filters, setFilters] = useState<AlertFilters>({ status: 'open' })
  const pager = useCursorPager()
  const alerts = useAlerts(filters, pager.cursor)
  const counts = alerts.data?.counts

  function update(patch: Partial<AlertFilters>) {
    setFilters((current) => ({ ...current, ...patch }))
    pager.reset()
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Alerts"
        description="Failed and late deliveries, partial runs, high-severity signals on held issuers, and audit or job failures. Failed and late runs are also emailed to the ops address, at most once a day per provider."
      />

      <div className="mb-5 flex flex-wrap items-center gap-2 text-sm" aria-live="polite">
        {counts ? (
          <>
            <Badge tone={counts.open ? 'attention' : 'positive'} dot>
              {counts.open} open
            </Badge>
            <Badge tone="attention">{counts.critical} critical</Badge>
            <Badge tone="watch">{counts.warning} warning</Badge>
            <Badge tone="info">{counts.info} information</Badge>
          </>
        ) : null}
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={(event) => update({ status: event.target.value as AlertFilters['status'] })}
        />
        <Select
          label="Severity"
          options={SEVERITY_OPTIONS}
          value={filters.severity ?? ''}
          onChange={(event) => update({ severity: event.target.value })}
        />
        <Select
          label="Kind"
          options={KIND_OPTIONS}
          value={filters.kind ?? ''}
          onChange={(event) => update({ kind: event.target.value })}
        />
        <Select
          label="Source"
          options={PROVIDER_OPTIONS}
          value={filters.provider ?? ''}
          onChange={(event) => update({ provider: event.target.value })}
        />
      </div>

      {alerts.isError ? (
        <Callout tone="danger" role="alert">
          {errorMessage(alerts.error)}
        </Callout>
      ) : (
        <DataTable
          caption="Operational alerts"
          columns={COLUMNS}
          rows={alerts.data?.items ?? []}
          getRowId={(alert) => alert.id}
          loading={alerts.isPending || alerts.isPlaceholderData}
          empty={
            <EmptyState
              variant="plain"
              icon={<CircleCheck />}
              title={filters.status === 'open' ? 'No open alerts' : 'No alerts match'}
              description="Change the filters to see acknowledged or older alerts."
            />
          }
          pagination={{
            mode: 'server',
            hasPrevious: pager.hasPrevious,
            hasNext: Boolean(alerts.data?.next_cursor),
            onPrevious: pager.previous,
            onNext: () => pager.next(alerts.data?.next_cursor ?? null),
            summary: `Page ${pager.page}`,
          }}
        />
      )}
    </>
  )
}
